import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Search,
  Utensils,
  Workflow,
  LayoutDashboard,
  TrendingUp,
  Cpu,
  Wallet,
  Package,
  Users,
  ShieldCheck,
  BarChart3,
  Cog,
  CalendarClock,
  HeartHandshake,
} from "lucide-react";
import useSeo from "@/lib/useSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryForm from "@/components/EnquiryForm";

const HOW_I_HELP = [
  {
    id: "operational-review",
    icon: Search,
    title: "Operational Review",
    body: "An independent examination of how the business is actually operating, where control is weak and what deserves attention first.",
  },
  {
    id: "fb-performance",
    icon: Utensils,
    title: "F&B Performance",
    body: "Stock, purchasing, GP, waste, menu profitability, labour productivity and operational controls.",
  },
  {
    id: "systems-and-processes",
    icon: Workflow,
    title: "Systems & Processes",
    body: "Identify duplication, unnecessary manual work, bottlenecks and processes that depend too heavily on individual people.",
  },
  {
    id: "management-information",
    icon: LayoutDashboard,
    title: "Management Information",
    body: "Help management see the information that matters without hunting through spreadsheets, disconnected systems and multiple reports.",
  },
  {
    id: "revenue-opportunities",
    icon: TrendingUp,
    title: "Revenue Opportunities",
    body: "Examine the customer journey to identify opportunities to improve spend, conversion, retention and cross-selling.",
  },
  {
    id: "automation-and-ai",
    icon: Cpu,
    title: "Automation & AI",
    body: "Identify where modern technology can genuinely improve the operation, reduce repetitive work and provide better management intelligence.",
  },
];

const HEALTH_CHECK_AREAS = [
  { icon: Wallet, label: "Financial control" },
  { icon: Package, label: "Stock and purchasing" },
  { icon: Users, label: "People and productivity" },
  { icon: ShieldCheck, label: "Compliance" },
  { icon: BarChart3, label: "Management information" },
  { icon: Cog, label: "Systems and processes" },
  { icon: CalendarClock, label: "Peak-period readiness" },
  { icon: HeartHandshake, label: "Customer and revenue opportunities" },
];

export default function HospitalityConsulting() {
  useSeo({
    title:
      "Hospitality Operations & Systems Consultant | Linked2Marketing",
    description:
      "Hospitality operations and systems consulting backed by 35+ years of hands-on experience. Operational reviews, F&B performance, processes, management information, automation and AI.",
    canonical:
      "https://linked2marketing.com/hospitality-consulting",
    ogType: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "Linked2Marketing — Hospitality Operations & Systems Consulting",
      url: "https://linked2marketing.com/hospitality-consulting",
      areaServed: "United Kingdom",
      serviceType: [
        "Hospitality operations consulting",
        "F&B consultant",
        "Hotel operations consultant",
        "Restaurant operations consultant",
        "Hospitality systems consultant",
        "Hospitality operational review",
      ],
      provider: {
        "@type": "Organization",
        name: "Linked2Marketing",
        url: "https://linked2marketing.com",
      },
    },
  });

  return (
    <div
      data-testid="page-hospitality-consulting"
      className="min-h-screen bg-[#07090D] text-mute-100"
    >
      <Navbar />
      <main className="pt-28 sm:pt-32">
        <Hero />
        <HowIHelp />
        <PracticalApproach />
        <DiagnosisToImplementation />
        <OperationalHealthCheck />
        <EnquiryForm />
      </main>
      <Footer />
    </div>
  );
}

/* ---------------- Hero ---------------- */
function Hero() {
  return (
    <section
      data-testid="hc-hero"
      className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
    >
      <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
        <div
          className="absolute left-1/2 -translate-x-1/2 top-[-160px] w-[1200px] h-[700px] opacity-70 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
          }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-8">
          <span className="eyebrow text-gold">Hospitality consulting</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-tight">
            Experience on the floor.{" "}
            <em className="not-italic text-gradient-gold">Systems thinking</em>{" "}
            in the boardroom.
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="text-[16.5px] leading-[1.75] text-mute-200">
            Gavin Patterson spent more than 35 years in hospitality before moving into digital
            technology. He has owned and operated restaurants, bars and hotels, and understands
            the realities of staffing, kitchens, suppliers, margins, customers, service and cash
            flow.
          </p>
          <p className="mt-5 text-[15px] leading-[1.7] text-mute-300">
            Today, that experience is combined with process analysis, digital systems, automation
            and AI to help hospitality businesses operate with greater control, visibility and
            efficiency.
          </p>
          <Link
            to="/#enquiry"
            data-testid="hc-hero-cta"
            className="btn-primary mt-8 inline-flex"
          >
            Start a conversation
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------- How I Help ---------------- */
function HowIHelp() {
  return (
    <section
      data-testid="hc-how-i-help"
      className="relative mt-24 sm:mt-36"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <span className="eyebrow text-gold">How I help</span>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[48px] leading-[1.08] tracking-tight text-mute-100">
              Six areas where an experienced operator&rsquo;s eye — combined with modern systems —
              tends to change the numbers.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[15.5px] leading-[1.75] text-mute-200">
              Not every hospitality business needs every service. The starting point is usually a
              conversation about where the operation feels under most pressure.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {HOW_I_HELP.map((item, i) => (
            <HelpCard key={item.id} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function HelpCard({ item, index }) {
  const Icon = item.icon;
  return (
    <article
      data-testid={`hc-help-${item.id}`}
      className="group relative rounded-3xl glass p-7 sm:p-8 h-full overflow-hidden transition-colors duration-500 hover:border-gold/25"
    >
      <div
        className="absolute -top-24 -right-24 w-[280px] h-[280px] rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
        }}
      />
      <div className="relative flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gold/25 bg-gold/[0.05] text-gold">
          <Icon className="w-[18px] h-[18px]" />
        </span>
        <span className="text-[10.5px] uppercase tracking-[0.24em] text-mute-300">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
      <h3 className="relative mt-6 font-display text-[24px] leading-[1.18] text-mute-100">
        {item.title}
      </h3>
      <p className="relative mt-4 text-[14.5px] leading-[1.7] text-mute-200">
        {item.body}
      </p>
    </article>
  );
}

/* ---------------- A Practical Approach ---------------- */
function PracticalApproach() {
  return (
    <section
      data-testid="hc-practical-approach"
      className="relative mt-24 sm:mt-36"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="eyebrow text-gold">A practical approach</span>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[48px] leading-[1.08] tracking-tight text-mute-100">
              Understand first.{" "}
              <em className="not-italic text-gradient-gold">Improve second.</em>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-3xl glass p-7 sm:p-9">
              <p className="text-[16px] leading-[1.75] text-mute-200">
                Every hospitality business works differently. The starting point is not software,
                AI or a predetermined solution. It is understanding how the operation actually
                works.
              </p>
              <div className="hairline my-6" />
              <p className="text-[15.5px] leading-[1.75] text-mute-200">
                That means looking at the people, processes, numbers, systems and customer journey
                together. From there, priorities can be identified based on operational risk,
                financial impact and practical opportunity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- From Diagnosis to Implementation ---------------- */
function DiagnosisToImplementation() {
  return (
    <section
      data-testid="hc-diagnosis-to-implementation"
      className="relative mt-24 sm:mt-36"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl glass-strong p-8 sm:p-12 lg:p-14 relative overflow-hidden">
          <div
            className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-70"
            style={{
              background:
                "radial-gradient(closest-side, rgba(199,166,106,0.14), transparent 70%)",
            }}
          />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <span className="eyebrow text-gold">From diagnosis to implementation</span>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight text-mute-100">
                The work does not have to end with a{" "}
                <em className="not-italic text-gradient-gold">report</em>.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[15.5px] leading-[1.75] text-mute-200">
                Where appropriate, Gavin can work alongside owners and management teams to
                implement recommendations, redesign processes and develop the systems needed to
                support them.
              </p>
              <p className="mt-5 text-[15.5px] leading-[1.75] text-mute-200">
                This can range from short operational reviews to ongoing improvement and
                transformation projects.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Operational Health Check ---------------- */
function OperationalHealthCheck() {
  return (
    <section
      id="operational-health-check"
      data-testid="hc-health-check"
      className="relative mt-24 sm:mt-36 scroll-mt-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-6">
            <span className="eyebrow text-gold">Operational health check</span>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[48px] leading-[1.08] tracking-tight text-mute-100">
              Start with an{" "}
              <em className="not-italic text-gradient-gold">operational health check</em>.
            </h2>
            <p className="mt-6 text-[16px] leading-[1.75] text-mute-200">
              A structured review can quickly identify where operational control is strong, where
              problems may be developing and where opportunities are being missed.
            </p>
            <Link
              to="/#enquiry"
              data-testid="hc-health-check-cta"
              className="btn-primary mt-8 inline-flex"
            >
              Book an initial conversation
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <p className="mt-4 text-[12.5px] text-mute-300">
              A considered conversation, not a sales sequence.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl glass p-7 sm:p-9">
              <span className="text-[11px] uppercase tracking-[0.24em] text-gold">
                Areas typically covered
              </span>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {HEALTH_CHECK_AREAS.map((a) => {
                  const Icon = a.icon;
                  return (
                    <li
                      key={a.label}
                      className="flex items-start gap-3 text-[14.5px] leading-[1.55] text-mute-100"
                    >
                      <span className="mt-[2px] inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gold/25 bg-gold/[0.05] text-gold flex-none">
                        <Icon className="w-[14px] h-[14px]" />
                      </span>
                      <span>{a.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
